import "./setupAxios";
import { useState, useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Icon from '@mui/material/Icon';
import SoftBox from 'components/SoftBox';
import Sidenav from 'prefabsComponents/Sidenav';
import theme from 'assets/theme';
import routes from 'routes';
import { useSoftUIController, setOpenConfigurator } from 'context';
import brand from 'assets/images/logos/circle-logo.png'
import Swal from 'sweetalert2'; // Importa SweetAlert
import 'bootstrap/dist/css/bootstrap.min.css';
import 'assets/css/general.css';
import { AuthProvider } from './context/AuthContext';
import PrivateRoute from './routing/ProtectedRoute';
import AppRoutes from "./AppRoutes";

export default function App() {
    const [controller, dispatch] = useSoftUIController();
    const { miniSidenav, layout, openConfigurator, sidenavColor } = controller;
    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const { pathname } = useLocation();

    // Variables de inactividad
    const [counter, setCounter] = useState(10); // tiempo del contador regresivo
    const activityTimeoutRef = useRef(null);
    const countdownRef = useRef(null);
    const INACTIVITY_TIME = 3000000; // 30 segundos de inactividad para pruebas

    // Función para cerrar sesión eliminando el token de sesión
    const logout = () => {
        // Eliminar cookies estableciendo la fecha de expiración en el pasado
        document.cookie =
            'access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        document.cookie =
            'refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        window.location.href = '/login'; // Redirige al login
    };

    // Mostrar alerta de SweetAlert con contador regresivo
    const showInactivityAlert = () => {
        setCounter(30); // Reinicia el contador
        Swal.fire({
            title: 'Inactividad detectada',
            text: `¿Sigues ahí? Tu sesión se cerrará en ${counter} segundos.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Permanecer en sesión',
            cancelButtonText: 'Cerrar sesión',
            cancelButtonColor: '#f52727',
            didOpen: () => {
                countdownRef.current = setInterval(() => {
                    setCounter(prevCounter => {
                        if (prevCounter <= 1) {
                            clearInterval(countdownRef.current);
                            logout(); // Cierra la sesión si el contador llega a 0
                            Swal.close();
                        } else {
                            Swal.update({
                                text: `¿Sigues ahí? Tu sesión se cerrará en ${
                                    prevCounter - 1
                                } segundos.`,
                            });
                        }
                        return prevCounter - 1;
                    });
                }, 1000);
            },
        }).then(result => {
            if (result.isConfirmed) {
                resetInactivityTimeout(); // Si el usuario permanece en sesión, resetea el tiempo de inactividad
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                logout(); // Si el usuario decide cerrar sesión
            }
            clearInterval(countdownRef.current); // Limpia el contador en cualquier caso
        });
    };

    // Resetear temporizador de inactividad
    const resetInactivityTimeout = () => {
        clearTimeout(activityTimeoutRef.current);
        clearInterval(countdownRef.current);
        activityTimeoutRef.current = setTimeout(
            () => showInactivityAlert(),
            INACTIVITY_TIME
        );
    };

    // Manejo de actividad
    const handleActivity = () => resetInactivityTimeout();

    useEffect(() => {
        // Configurar eventos de actividad
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('visibilitychange', handleActivity);
        resetInactivityTimeout();

        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('visibilitychange', handleActivity);
            clearTimeout(activityTimeoutRef.current);
            clearInterval(countdownRef.current);
        };
    }, []);

    const handleConfiguratorOpen = () =>
        setOpenConfigurator(dispatch, !openConfigurator);

    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    const getRoutes = allRoutes =>
        allRoutes.map(route => {
          // Si la ruta está marcada como invisible, no se renderiza
          if (route.visible === false) return null;
      
          if (route.collapse) {
            // Recursividad para filtrar también las subrutas
            return getRoutes(route.collapse);
          }
      
          if (route.route) {
            return (
              <Route
                key={route.key}
                path={route.route}
                element={
                  route.protected ? (
                    <PrivateRoute element={route.component} />
                  ) : (
                    route.component
                  )
                }
              />
            );
          }
          return null;
        });      

    const configsButton = (
        <SoftBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="3.5rem"
            height="3.5rem"
            bgColor="white"
            shadow="sm"
            borderRadius="50%"
            position="fixed"
            right="2rem"
            bottom="2rem"
            zIndex={99}
            color="dark"
            sx={{ cursor: 'pointer' }}
            onClick={handleConfiguratorOpen}
        >
            <Icon fontSize="default" color="inherit">
                settings
            </Icon>
        </SoftBox>
    );

    return (
        <AuthProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                {layout === 'dashboard' && (
                    <Sidenav
                        color={sidenavColor}
                        brand={brand}
                        brandName="CARGONAUTS"
                        routes={routes}
                    />
                )}
                <AppRoutes/>
            </ThemeProvider>
        </AuthProvider>
    );
}
